/** The building blocks of the server-written pages: Know your data and the
 *  Cash engine. The server writes every word and number as typed blocks
 *  inside sections (components/know-your-data/api.ts); these only draw them.
 *  "Ask about this" opens a new chat with the question typed in; "Run the
 *  analysis" opens a ready-made analysis's form.
 */
import {
  AlertTriangle,
  Check,
  Loader2,
  MessageCircle,
  Play
} from 'lucide-react';
import { ReactNode, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChatInteract } from '@chainlit/react-client';

import {
  BarRow,
  Block,
  Lang,
  Note,
  Section,
  chatHref,
  skillHref
} from '@/components/know-your-data/api';

export const surface = {
  background: 'var(--sig-surface)',
  borderColor: 'var(--sig-border)'
};

/** The page's language, for the few words this file writes itself (buttons). */
export const LangContext = createContext<Lang>('en');

export function LanguageSwitch({
  lang,
  onChange
}: {
  lang: Lang;
  onChange: (l: Lang) => void;
}) {
  const item = (value: Lang, label: string) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={lang === value}
      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        lang === value
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div
      className="flex shrink-0 gap-1 rounded-full border p-0.5"
      style={surface}
      dir="ltr"
    >
      {item('en', 'English')}
      {item('ar', 'العربية')}
    </div>
  );
}

export function Building({
  lang,
  building
}: {
  lang: Lang;
  building: boolean;
}) {
  return (
    <div
      className="grid place-items-center gap-3 rounded-xl border px-6 py-16 text-center"
      style={surface}
    >
      <Loader2 className="size-5 animate-spin text-muted-foreground" />
      {building ? (
        <p className="max-w-sm text-sm text-muted-foreground">
          {lang === 'ar'
            ? 'Signals بيحسب بياناتك دلوقتي. أول مرة بعد التحديث الليلي بتاخد لحد دقيقة.'
            : 'Signals is counting your data. The first view after each nightly update takes up to a minute.'}
        </p>
      ) : null}
    </div>
  );
}

// Sections that are only buttons into the chat (run an analysis, ask a
// question): nothing to read on paper, so Save as PDF leaves them out.
const ON_SCREEN_ONLY = ['skills', 'ask'];

export function SectionView({
  section,
  children
}: {
  section: Section;
  children?: ReactNode;
}) {
  return (
    <section
      id={`kyd-${section.id}`}
      className={`scroll-mt-14 space-y-3 border-t pt-6 first:border-t-0 first:pt-2 ${
        ON_SCREEN_ONLY.includes(section.id) ? 'print:hidden' : ''
      }`}
    >
      <div className="space-y-0.5">
        <h2 className="text-lg font-semibold text-[var(--sig-ink)] print:break-after-avoid">
          {section.title}
        </h2>
        {section.intro ? (
          <p className="text-sm text-muted-foreground">{section.intro}</p>
        ) : null}
      </div>
      {children}
      {section.blocks.map((b, i) => (
        // A card is never split across two printed pages.
        <div key={i} className="print:break-inside-avoid">
          <BlockView block={b} />
        </div>
      ))}
    </section>
  );
}

// ── Blocks ───────────────────────────────────────────────────────────────────

export function useAsk() {
  const navigate = useNavigate();
  const { clear } = useChatInteract();
  return {
    ask: (question: string) => {
      // A new conversation, as "Ask Signals why" does: the question is about
      // this page, not about whatever chat was open before.
      clear();
      navigate(chatHref(question));
    },
    run: (skill: string) => {
      clear();
      navigate(skillHref(skill));
    }
  };
}

/** A figure: kept in one piece inside a sentence of the other direction.
 *  "auto", not "ltr": "1.1 مليون" must read number-then-word in Arabic. */
function Num({ children }: { children: ReactNode }) {
  return (
    <span
      dir="auto"
      className="tabular-nums"
      style={{ unicodeBidi: 'isolate' }}
    >
      {children}
    </span>
  );
}

export function BlockView({ block }: { block: Block }) {
  switch (block.type) {
    case 'table':
      return (
        <div className="overflow-x-auto rounded-xl border" style={surface}>
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-xs text-muted-foreground"
                style={{ background: 'var(--sig-table-head)' }}
              >
                {block.head.map((h) => (
                  <th key={h} className="px-3 py-2 text-start font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r) => (
                <tr
                  key={r.label}
                  className="border-t"
                  style={{ borderColor: 'var(--sig-border-row)' }}
                >
                  <td className="px-3 py-2 text-[var(--sig-ink)]">
                    <bdi>{r.label}</bdi>
                  </td>
                  <td className="px-3 py-2 font-medium text-[var(--sig-ink)]">
                    <Num>{r.value}</Num>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.meta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'checks':
      return (
        <ul className="space-y-2">
          {block.items.map((c, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm text-[var(--sig-body)]"
            >
              {c.state === 'ok' ? (
                <Check
                  className="mt-0.5 size-4 shrink-0"
                  style={{ color: 'var(--sig-positive)' }}
                />
              ) : (
                <AlertTriangle
                  className="mt-0.5 size-4 shrink-0"
                  style={{ color: 'var(--sig-warn-icon)' }}
                />
              )}
              <span>{c.text}</span>
            </li>
          ))}
        </ul>
      );
    case 'bars':
      return (
        <div className="space-y-3 rounded-xl border p-4" style={surface}>
          <div>
            <h3 className="text-sm font-semibold text-[var(--sig-ink)]">
              {block.title}
            </h3>
            {block.caption ? (
              <p className="text-xs text-muted-foreground">{block.caption}</p>
            ) : null}
          </div>
          <Bars rows={block.rows} />
        </div>
      );
    case 'callout':
      return (
        <div
          className="flex items-center gap-3 rounded-xl px-4 py-3"
          style={{ background: 'var(--sig-tint)' }}
        >
          <span
            className="text-2xl font-bold"
            style={{ color: 'hsl(var(--primary))' }}
            dir="ltr"
          >
            {block.big}
          </span>
          <p className="text-sm text-[var(--sig-ink)]">{block.text}</p>
        </div>
      );
    case 'notes':
      return (
        <ul className="divide-y rounded-xl border" style={surface}>
          {block.items.map((n, i) => (
            <NoteView key={i} note={n} />
          ))}
        </ul>
      );
    case 'finding':
      return <Finding block={block} />;
    case 'event':
      return <EventCard block={block} />;
    case 'signal':
      return (
        <div
          className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 rounded-xl border px-4 py-3"
          style={surface}
        >
          <span className="text-sm font-semibold text-[var(--sig-ink)]">
            {block.title}
          </span>
          <Spark values={block.spark} />
          <span className="text-sm text-[var(--sig-ink)]">
            <Num>{block.value}</Num>{' '}
            <span className="text-xs text-muted-foreground">{block.unit}</span>
            {block.move ? (
              <span className="ms-2 text-xs text-muted-foreground">
                {block.move}
              </span>
            ) : null}
          </span>
          <span />
          {block.why ? (
            <p className="col-span-2 text-sm text-[var(--sig-body)]">
              {block.why}
            </p>
          ) : null}
          <span className="col-span-2 text-[11px] text-muted-foreground">
            {block.source}
          </span>
        </div>
      );
    case 'countdown':
      return (
        <div
          className="flex items-center gap-3 rounded-xl border px-4 py-3"
          style={surface}
        >
          <div
            className="shrink-0 rounded-lg px-3 py-1.5 text-center"
            style={{ background: 'var(--sig-tint)' }}
          >
            <span
              className="block text-xl font-bold leading-tight"
              style={{ color: 'hsl(var(--primary))' }}
              dir="ltr"
            >
              {block.value}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {block.unit}
            </span>
          </div>
          <p className="text-sm text-[var(--sig-ink)]">{block.text}</p>
        </div>
      );
    case 'progress':
      return (
        <div
          className="space-y-2 rounded-xl border p-4"
          style={{
            background: 'var(--sig-chip)',
            borderColor: 'var(--sig-border-blue)'
          }}
        >
          <h3 className="text-sm font-semibold text-[var(--sig-ink)]">
            {block.title}
          </h3>
          <p className="flex gap-2 text-sm text-[var(--sig-body)]">
            <Check
              className="mt-0.5 size-4 shrink-0"
              style={{ color: 'var(--sig-positive)' }}
            />
            <span>{block.now}</span>
          </p>
          <p className="flex gap-2 text-sm text-[var(--sig-body)]">
            <Loader2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <span>{block.later}</span>
          </p>
          <div className="h-2 overflow-hidden rounded bg-muted">
            <div
              className="h-full rounded"
              style={{
                width: `${(100 * block.done) / block.total}%`,
                background: 'var(--sig-blue)'
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{block.left}</span>
            <span>{block.right}</span>
          </div>
        </div>
      );
    case 'skills':
      return <Skills block={block} />;
    case 'blind':
      return (
        <ul className="space-y-2">
          {block.items.map((b, i) => (
            <li
              key={i}
              className="space-y-0.5 rounded-xl border border-dashed px-4 py-3"
              style={surface}
            >
              <p className="text-sm font-semibold text-[var(--sig-ink)]">
                {b.title}
              </p>
              <p className="text-sm text-[var(--sig-body)]">{b.text}</p>
              {b.so ? (
                <p className="text-xs" style={{ color: 'hsl(var(--primary))' }}>
                  {b.so}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      );
    case 'questions':
      return <Questions block={block} />;
    default:
      return null;
  }
}

function Bars({ rows }: { rows: BarRow[] }) {
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span
              className="min-w-0 truncate text-[var(--sig-ink)]"
              title={r.label}
            >
              <bdi>{r.label}</bdi>
            </span>
            <span className="shrink-0 font-medium text-[var(--sig-body)]">
              <Num>{r.display}</Num>
            </span>
          </div>
          <div
            className="h-2.5 rounded bg-muted"
            title={`${r.label}: ${r.display}`}
          >
            <div
              className="h-full rounded"
              style={{
                width: `${Math.max(1, Math.min(100, r.share * 100))}%`,
                background: 'var(--sig-blue)'
              }}
            />
          </div>
          {r.meta ? (
            <p className="text-xs text-muted-foreground">{r.meta}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function NoteView({ note }: { note: Note }) {
  return (
    <li
      className="space-y-1.5 px-4 py-3"
      style={{ borderColor: 'var(--sig-border-row)' }}
    >
      <p className="text-sm font-semibold text-[var(--sig-ink)]">
        {note.title}
      </p>
      <p className="text-sm text-[var(--sig-body)]">{note.text}</p>
      {note.week ? (
        <div className="overflow-x-auto">
          <div
            className="grid min-w-[300px] items-center gap-1 text-[11px] text-muted-foreground"
            style={{ gridTemplateColumns: '5rem repeat(7, minmax(0, 1fr))' }}
          >
            <span />
            {note.week.days.map((d) => (
              <span key={d} className="text-center">
                {d}
              </span>
            ))}
            {note.week.rows.map((r) => (
              <WeekRow key={r.label} label={r.label} open={r.open} />
            ))}
          </div>
        </div>
      ) : null}
      {note.bars ? <Bars rows={note.bars} /> : null}
      {note.so ? (
        <p className="text-xs" style={{ color: 'hsl(var(--primary))' }}>
          {note.so}
        </p>
      ) : null}
    </li>
  );
}

function WeekRow({ label, open }: { label: string; open: boolean[] }) {
  return (
    <>
      <span>{label}</span>
      {open.map((on, i) => (
        <span
          key={i}
          className="h-3.5 rounded"
          style={
            on
              ? { background: 'var(--sig-blue)' }
              : { border: '1.5px dashed var(--sig-subtle)' }
          }
        />
      ))}
    </>
  );
}

function Finding({ block }: { block: Extract<Block, { type: 'finding' }> }) {
  const { ask, run } = useAsk();
  return (
    <article
      className="space-y-2.5 rounded-xl border p-4"
      style={{ ...surface, boxShadow: 'var(--sig-shadow-card)' }}
    >
      {block.kpi ? (
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-2xl font-bold text-[var(--sig-ink)]">
            <Num>{block.kpi}</Num>
          </span>
          {block.kpi_label ? (
            <span className="text-sm text-muted-foreground">
              {block.kpi_label}
            </span>
          ) : null}
        </div>
      ) : null}
      <h3 className="text-[15px] font-semibold leading-snug text-[var(--sig-ink)]">
        {block.title}
      </h3>
      <p className="text-sm text-[var(--sig-body)]">{block.text}</p>
      {block.diverging ? <Diverging data={block.diverging} /> : null}
      <div className="flex flex-wrap gap-2">
        {block.ask ? (
          <AskButton onClick={() => ask(block.ask!.question)} />
        ) : null}
        {block.skill ? (
          <button
            type="button"
            onClick={() => run(block.skill!)}
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium text-[var(--sig-body)] hover:bg-muted print:hidden"
            style={{ borderColor: 'var(--sig-border-strong)' }}
          >
            <Play className="size-3.5" />
            <AnalysisLabel />
          </button>
        ) : null}
      </div>
      {block.source ? (
        <p className="text-[11px] text-muted-foreground">{block.source}</p>
      ) : null}
    </article>
  );
}

function AskButton({ onClick }: { onClick: () => void }) {
  const ar = useContext(LangContext) === 'ar';
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-[var(--sig-tint)] print:hidden"
      style={{
        background: 'var(--sig-chip)',
        borderColor: 'var(--sig-border-blue)',
        color: 'hsl(var(--primary))'
      }}
    >
      <MessageCircle className="size-3.5" />
      {ar ? 'اسأل عن ده' : 'Ask about this'}
    </button>
  );
}

function AnalysisLabel() {
  return (
    <span>
      {useContext(LangContext) === 'ar' ? 'شغّل التحليل' : 'Run the analysis'}
    </span>
  );
}

function Diverging({
  data
}: {
  data: NonNullable<Extract<Block, { type: 'finding' }>['diverging']>;
}) {
  const values = data.rows.map((r) => r.value).concat([data.reference, 0]);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const at = (v: number) => ((v - lo) / span) * 100;
  const zero = at(0);
  return (
    <div className="space-y-2">
      {data.rows.map((r) => {
        const start = Math.min(zero, at(r.value));
        const width = Math.abs(at(r.value) - zero);
        return (
          <div
            key={r.label}
            className="grid items-center gap-2 text-sm"
            style={{ gridTemplateColumns: 'minmax(0, 7rem) 1fr 3.5rem' }}
          >
            <span className="truncate text-[var(--sig-ink)]" title={r.label}>
              <bdi>{r.label}</bdi>
            </span>
            <span className="relative h-3" title={`${r.label}: ${r.display}`}>
              <span
                className="absolute inset-y-[-3px] w-px bg-border"
                style={{ insetInlineStart: `${zero}%` }}
              />
              <span
                className="absolute inset-y-0 rounded-sm"
                style={{
                  insetInlineStart: `${start}%`,
                  width: `${Math.max(width, 0.5)}%`,
                  background:
                    r.value < 0 ? 'var(--sig-negative)' : 'var(--sig-blue)'
                }}
              />
              <span
                className="absolute inset-y-[-5px] border-s border-dashed"
                style={{
                  insetInlineStart: `${at(data.reference)}%`,
                  borderColor: 'var(--sig-muted)'
                }}
              />
            </span>
            <span
              className="text-end text-xs font-medium"
              style={{
                color: r.value < 0 ? 'var(--sig-negative)' : 'var(--sig-body)'
              }}
            >
              <Num>{r.display}</Num>
            </span>
          </div>
        );
      })}
      <p className="text-[11px] text-muted-foreground">
        ┆ <Num>{data.reference_label}</Num>
      </p>
    </div>
  );
}

function EventCard({ block }: { block: Extract<Block, { type: 'event' }> }) {
  const { ask } = useAsk();
  return (
    <article className="space-y-2 rounded-xl border p-4" style={surface}>
      <h3 className="text-sm font-semibold text-[var(--sig-ink)]">
        {block.title}
      </h3>
      <p className="text-sm text-[var(--sig-body)]">{block.text}</p>
      {block.ask ? (
        <AskButton onClick={() => ask(block.ask!.question)} />
      ) : null}
    </article>
  );
}

function Spark({ values }: { values: number[] }) {
  if (!values || values.length < 2) return <span />;
  const w = 84;
  const h = 26;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const points = values
    .map(
      (v, i) =>
        `${((i / (values.length - 1)) * (w - 4) + 2).toFixed(1)},${(h - 2 - ((v - lo) / span) * (h - 4)).toFixed(1)}`
    )
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke="var(--sig-blue)"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Skills({ block }: { block: Extract<Block, { type: 'skills' }> }) {
  const { run } = useAsk();
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {block.items.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => run(s.id)}
          className="space-y-1 rounded-xl border p-3 text-start transition-colors hover:border-[var(--sig-blue)]"
          style={surface}
        >
          <span className="flex items-center gap-1.5 text-sm font-semibold text-[var(--sig-ink)]">
            <Play
              className="size-3.5 shrink-0"
              style={{ color: 'var(--sig-blue)' }}
            />
            {s.title}
          </span>
          {s.text ? (
            <span
              className="block text-xs text-muted-foreground"
              dir="ltr"
              style={{ textAlign: 'start' }}
            >
              {s.text}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function Questions({
  block
}: {
  block: Extract<Block, { type: 'questions' }>;
}) {
  const { ask } = useAsk();
  return (
    <div className="flex flex-wrap gap-2">
      {block.items.map((q) => (
        <button
          key={q.question}
          type="button"
          onClick={() => ask(q.question)}
          dir="auto"
          className="rounded-xl border px-3 py-2 text-start text-sm text-[var(--sig-ink)] hover:border-[var(--sig-blue)]"
          style={surface}
        >
          {q.question}
        </button>
      ))}
    </div>
  );
}
